import { DISPLAY_BY_KEY, DISPLAY_COLUMNS, FILTERABLE_BY_FILTER_KEY } from './columnRegistry';
import { dropRetiredFilterValue, parseSortParam, serializeSort } from './searchParamCodec';
import { own } from '../../utils/ownKey';

/**
 * Plan 101 — per-browser Device List preferences (localStorage), following
 * the tabConfig.ts pattern (try/catch, validate on read).
 *
 * Two independent restore paths:
 *  - URL-backed data view (filters / sort / pageSize / q): stored in the same
 *    encoding as the URL params and injected ONLY when the URL is bare — a
 *    shared link always reproduces the sender's data view.
 *  - Layout (columnOrder / hidden / widths): client-only, never in the URL,
 *    ALWAYS restored — a shared link never controls your column layout.
 *
 * FS-889: preferences are stored per account (`deviceListPrefs.v1.<userId>`)
 * so one account's filters never show for another on a shared browser; the
 * old account-less key gives up only its column layout, once. The same
 * `decodeView` reads a saved report, listing what it had to change so the page
 * can say so instead of quietly showing more devices.
 *
 * `columnOrder` is the full ordered list of every registry key the user has
 * seen; `hidden` is the user-hidden set. Keeping them separate lets a NEW
 * registry column appear at its registry position per its `defaultVisible`
 * instead of being mistaken for one the user hid.
 */

/** Everything a report saves and the page restores: layout plus data view. */
export interface DeviceListView {
  columnOrder: string[];
  hidden: string[];
  widths: Record<string, number>;
  filters: Record<string, string>;
  sort: string;
  pageSize: number;
  q: string;
}

export interface DeviceListPrefs extends DeviceListView {
  /** The report open when the view was last written; null for an unsaved view. */
  reportId: number | null;
}

/** The pre-FS-889 key; read once per account for its layout, never written. */
export const LEGACY_PREFS_KEY = 'deviceListPrefs.v1';

export const prefsKey = (userId: number): string => `${LEGACY_PREFS_KEY}.${userId}`;

export const PAGE_SIZES = [20, 50, 100, 200];
const DEFAULT_PAGE_SIZE = 20;

/**
 * Column widths a REPORT may carry, in px (the server's rule: whole pixels,
 * 20..2000). The page itself and the per-account prefs have no upper bound:
 * the resize handle lets a column grow as wide as the drag, and a layout
 * saved before reports existed is kept exactly as it was.
 */
export const MIN_SAVED_WIDTH = 20;
export const MAX_SAVED_WIDTH = 2000;

export function defaultView(): DeviceListView {
  return {
    columnOrder: DISPLAY_COLUMNS.map((c) => c.key),
    hidden: DISPLAY_COLUMNS.filter((c) => !c.defaultVisible).map((c) => c.key),
    widths: {},
    filters: {},
    sort: '',
    pageSize: DEFAULT_PAGE_SIZE,
    q: '',
  };
}

export function defaultPrefs(): DeviceListPrefs {
  return { ...defaultView(), reportId: null };
}

/**
 * Merge a saved column order with the current registry: unknown saved keys are
 * dropped; registry keys missing from the saved order (columns added since the
 * prefs were written) are spliced in right after their nearest preceding
 * registry sibling, so new columns land where the registry intends without
 * disturbing the user's ordering.
 */
export function mergeColumnOrder(saved: string[]): string[] {
  const order = saved.filter((k) => own(DISPLAY_BY_KEY, k));
  const present = new Set(order);
  DISPLAY_COLUMNS.forEach((col, regIdx) => {
    if (present.has(col.key)) return;
    let insertAt = 0;
    for (let i = regIdx - 1; i >= 0; i--) {
      const prevIdx = order.indexOf(DISPLAY_COLUMNS[i].key);
      if (prevIdx !== -1) {
        insertAt = prevIdx + 1;
        break;
      }
    }
    order.splice(insertAt, 0, col.key);
    present.add(col.key);
  });
  return order;
}

/**
 * The hidden set for rendering: saved user hides (validated) plus any column
 * the user has never seen whose registry default is hidden.
 */
export function effectiveHidden(savedOrder: string[], savedHidden: string[]): string[] {
  const seen = new Set(savedOrder);
  const hidden = new Set(savedHidden.filter((k) => own(DISPLAY_BY_KEY, k)));
  for (const col of DISPLAY_COLUMNS) {
    if (!seen.has(col.key) && !col.defaultVisible) hidden.add(col.key);
  }
  return [...hidden];
}

export type DecodeStatus = 'ok' | 'adjusted' | 'unsupported';

export interface DecodedView {
  /**
   * `ok`: applied as saved. `adjusted`: something saved no longer exists and
   * was dropped — `adjustments` says what, in words for the operator.
   * `unsupported`: a report saved by a newer console (`v` is not 1); never
   * applied and never overwritten, `effective` is the default view.
   */
  status: DecodeStatus;
  effective: DeviceListView;
  adjustments: string[];
}

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

const stringList = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((k): k is string => typeof k === 'string') : [];

/** Dedupe, keeping the first occurrence. */
const unique = (keys: string[]): string[] => [...new Set(keys)];

const filterLabel = (key: string): string => own(FILTERABLE_BY_FILTER_KEY, key)?.label ?? key;

/**
 * Validate a saved view (localStorage prefs or a report's state) against the
 * current registry. Pure: the same input always decodes the same way.
 *
 * Own-property lookups throughout — a key is data from storage or the server,
 * and `constructor` or `__proto__` must read as unknown, not as a function.
 * Ordered keys are deduplicated; a width is any finite number of pixels for
 * prefs (what the page produces and always kept) and a whole number inside
 * MIN..MAX_SAVED_WIDTH for a report (what the server stores); unknown
 * columns, filters and sort keys are dropped; retired filter values go
 * (FS-752); the sort is re-serialized canonically; an unknown page size
 * falls back to the default.
 *
 * `mode: 'report'` also requires `v === 1`, else the result is `unsupported`.
 */
export function decodeView(raw: unknown, mode: 'prefs' | 'report' = 'prefs'): DecodedView {
  const defaults = defaultView();
  if (!isObject(raw)) {
    return { status: mode === 'report' ? 'unsupported' : 'adjusted', effective: defaults, adjustments: [] };
  }
  if (mode === 'report' && raw.v !== 1) {
    return { status: 'unsupported', effective: defaults, adjustments: [] };
  }
  const adjustments: string[] = [];

  const savedOrder = unique(stringList(raw.columnOrder));
  for (const k of savedOrder) {
    if (!own(DISPLAY_BY_KEY, k)) adjustments.push(`the "${k}" column no longer exists`);
  }
  const savedHidden = unique(stringList(raw.hidden));

  const widths: Record<string, number> = {};
  if (isObject(raw.widths)) {
    for (const [k, v] of Object.entries(raw.widths)) {
      if (!own(DISPLAY_BY_KEY, k)) continue;
      if (typeof v !== 'number' || !Number.isFinite(v)) continue;
      if (mode === 'report' && (!Number.isInteger(v) || v < MIN_SAVED_WIDTH || v > MAX_SAVED_WIDTH)) continue;
      widths[k] = v;
    }
  }

  const filters: Record<string, string> = {};
  if (isObject(raw.filters)) {
    for (const [k, v] of Object.entries(raw.filters)) {
      if (typeof v !== 'string' || v === '') continue;
      if (!own(FILTERABLE_BY_FILTER_KEY, k)) {
        adjustments.push(`the "${k}" filter no longer exists`);
        continue;
      }
      const kept = dropRetiredFilterValue(k, v);
      if (kept === '') {
        adjustments.push(`the ${filterLabel(k)} filter named only values that no longer exist`);
      } else {
        if (kept !== v) adjustments.push(`values that no longer exist were removed from the ${filterLabel(k)} filter`);
        filters[k] = kept;
      }
    }
  }

  let sort = '';
  if (typeof raw.sort === 'string' && raw.sort.trim() !== '') {
    sort = serializeSort(parseSortParam(raw.sort));
    if (sort !== raw.sort.trim()) adjustments.push('part of the sort order is no longer available');
  }

  let pageSize = DEFAULT_PAGE_SIZE;
  if (PAGE_SIZES.includes(raw.pageSize as number)) pageSize = raw.pageSize as number;
  else if (raw.pageSize !== undefined) adjustments.push(`page size ${String(raw.pageSize)} is not available`);

  const q = typeof raw.q === 'string' ? raw.q : '';

  return {
    status: adjustments.length > 0 ? 'adjusted' : 'ok',
    effective: {
      columnOrder: mergeColumnOrder(savedOrder),
      hidden: effectiveHidden(savedOrder, savedHidden),
      widths,
      filters,
      sort,
      pageSize,
      q,
    },
    adjustments,
  };
}

const parseStored = (key: string): unknown => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const p: unknown = JSON.parse(raw);
    return isObject(p) && Array.isArray(p.columnOrder) ? p : null;
  } catch {
    return null;
  }
};

/**
 * Read + validate this account's saved prefs; null when absent/corrupt (use
 * defaults). An account with no entry of its own starts from the layout —
 * ONLY the layout — of the pre-FS-889 account-less key: its filters, sort,
 * page size and search text may belong to someone else.
 */
export function readPrefs(userId: number): DeviceListPrefs | null {
  const mine = parseStored(prefsKey(userId));
  if (mine) {
    const { effective } = decodeView(mine);
    const reportId = (mine as Record<string, unknown>).reportId;
    return {
      ...effective,
      reportId: typeof reportId === 'number' && Number.isInteger(reportId) && reportId > 0 ? reportId : null,
    };
  }
  const legacy = parseStored(LEGACY_PREFS_KEY);
  if (!legacy) return null;
  const { effective } = decodeView(legacy);
  return {
    ...defaultPrefs(),
    columnOrder: effective.columnOrder,
    hidden: effective.hidden,
    widths: effective.widths,
  };
}

export function writePrefs(userId: number, prefs: DeviceListPrefs): void {
  try {
    localStorage.setItem(prefsKey(userId), JSON.stringify(prefs));
  } catch {
    /* localStorage unavailable (private mode) — prefs are best-effort */
  }
}
